import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ENGINES } from '@/engines/registry';
import { cn } from '@/lib/utils';

/* ----------------------------------------------------------------------------
 * Command palette (Cmd/Ctrl-K) — the universal teleporter (brief §5.1/§5.6).
 * PLACEHOLDER scope: navigates to engines/capabilities/settings. The NL-command
 * → copilot path is stubbed (routes to the copilot rail). Wire real search +
 * record results when the API lands.
 * -------------------------------------------------------------------------- */

interface Command {
  id: string;
  label: string;
  hint: string;
  run: () => void;
}

export function CommandPalette({
  open,
  onClose,
  onCopilot,
}: {
  open: boolean;
  onClose: () => void;
  onCopilot: (q: string) => void;
}) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!open) setQ('');
  }, [open]);

  // ESC closes the palette (accessible dismissal, matches the ESC hint).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const commands = useMemo<Command[]>(() => {
    const list: Command[] = [
      { id: 'home', label: 'Executive OS', hint: 'Home · mission control', run: () => navigate('/') },
      { id: 'settings', label: 'Settings', hint: 'Workspace · members · compliance', run: () => navigate('/settings') },
    ];
    for (const e of ENGINES) {
      list.push({
        id: `engine-${e.key}`,
        label: e.name,
        hint: e.status === 'ga' ? 'Open engine' : `${e.status}`,
        run: () => navigate(e.base),
      });
      // Flatten one level of subnav so grouped capabilities (e.g. SEO →
      // Search Performance → Google Search Console) stay teleportable.
      const flat = e.capabilities.flatMap((c) => [c, ...(c.children ?? [])]);
      for (const c of flat) {
        if (c.path)
          list.push({
            id: `cap-${e.key}-${c.key}`,
            label: `${e.short} → ${c.label}`,
            hint: 'Capability',
            run: () => navigate(c.path!),
          });
      }
    }
    return list;
  }, [navigate]);

  const filtered = q
    ? commands.filter((c) => (c.label + c.hint).toLowerCase().includes(q.toLowerCase()))
    : commands;

  if (!open) return null;

  const nlCommand = q.trim().length > 0 && filtered.length === 0;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center px-4 pt-[12vh]" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-scrim/40 animate-fade-in" onClick={onClose} aria-hidden />
      <div className="relative w-full max-w-xl overflow-hidden rounded-xl border border-border-default bg-surface shadow-lg animate-fade-in">
        <div className="flex items-center gap-2 border-b border-border-subtle px-4">
          <span className="text-text-tertiary">⌕</span>
          <input
            autoFocus
            aria-label="Search records, engines, and settings, or ask the copilot"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if (nlCommand) {
                  onCopilot(q);
                  onClose();
                } else if (filtered[0]) {
                  filtered[0].run();
                  onClose();
                }
              }
            }}
            placeholder="Search records, engines, settings — or ask the copilot…"
            className="h-12 flex-1 bg-transparent text-sm text-text-primary outline-none placeholder:text-text-tertiary"
          />
          <kbd className="rounded border border-border-default px-1.5 py-0.5 text-[10px] text-text-tertiary">
            ESC
          </kbd>
        </div>
        <ul className="max-h-80 overflow-y-auto p-1.5">
          {nlCommand && (
            <li>
              <button
                onClick={() => {
                  onCopilot(q);
                  onClose();
                }}
                className="flex w-full items-center gap-2 rounded-md border border-ai/30 bg-ai-wash/50 px-3 py-2 text-left text-sm"
              >
                <span className="text-ai" aria-hidden>✧</span>
                Ask the copilot: <span className="font-medium text-text-primary">“{q}”</span>
              </button>
            </li>
          )}
          {filtered.map((c, i) => (
            <li key={c.id}>
              <button
                onClick={() => {
                  c.run();
                  onClose();
                }}
                className={cn(
                  'flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm hover:bg-hover',
                  i === 0 && !nlCommand && 'bg-inset',
                )}
              >
                <span className="text-text-primary">{c.label}</span>
                <span className="text-[11px] text-text-tertiary">{c.hint}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
